import * as THREE from 'three';
import { CLASS_WEAPON, GEAR_BASES, type GearBaseId, type HeroClass } from '@somnhold/content';
import { DSTEEL, LEATHER_BELT, cloth, metal, spectral, std } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { aura } from './hero-tiers.ts';
import { CLOTH_A, CLOTH_B, CLOTH_TRIM, HIDE, HIDE_D, MOSS, MOSS_DK, PLATE, VIGIL_EYE, WATCH_DK, gearHeld, type GearRarity } from './gear.ts';
import type { HeroRig, Model } from './types.ts';
import { burntClasp } from './biblio.ts';

/*
 * A hero dressed in what it wears (docs/EQUIPO.md §10, etapa 3). The class's builder draws its usual kit;
 * this swaps what is in its hands for the pieces it actually carries, before the model is baked (so it
 * costs nothing more per frame than the class's own model). The look comes from the sim's heroLook:
 * main ~ rarity ~ off hand ~ rarity ~ head ~ chest ~ feet, `-` for nothing worn and `u` for a unique piece (the
 * class's own model stands for those).
 */

export interface Look {
  main: GearBaseId | 'u' | null;
  rarity: GearRarity;
  off: GearBaseId | 'u' | null;
  offRarity: GearRarity;
  head: GearBaseId | 'u' | null;
  chest: GearBaseId | 'u' | null;
  feet: GearBaseId | 'u' | null;
  /** The titan's mark (level 50): its own glow. */
  titan: boolean;
  /**
   * The path's look (docs/SENDAS.md): pieces earned so far, one a chapter (5: the whole look). Read here, drawn in
   * stage 2 with each class's own pieces; until then the miniature is as it was.
   */
  senda: number;
}

const RARITIES: readonly string[] = ['common', 'uncommon', 'rare', 'veryRare', 'unique'];
const piece = (s: string | undefined): GearBaseId | 'u' | null => (!s || s === '-' ? null : s === 'u' ? 'u' : s in GEAR_BASES ? (s as GearBaseId) : null);

/** A look string (heroLook) read back; null when there is none. A piece in the wrong slot reads as nothing. */
export const parseLook = (s: string | undefined): Look | null => {
  if (!s) return null;
  const [main, rarity, off, offRarity, head, chest, feet, mark, path] = s.split('~');
  const rar = (r: string | undefined): GearRarity => (RARITIES.includes(r ?? '') ? r : 'common') as GearRarity;
  const worn = (x: string | undefined, slot: 'head' | 'armor' | 'boots'): GearBaseId | 'u' | null => {
    const p = piece(x);
    return p && p !== 'u' && GEAR_BASES[p].slot !== slot ? null : p;
  };
  return {
    main: piece(main), rarity: rar(rarity), off: piece(off), offRarity: rar(offRarity),
    head: worn(head, 'head'), chest: worn(chest, 'armor'), feet: worn(feet, 'boots'),
    titan: mark === 'T',
    senda: path?.startsWith('S') ? Math.max(0, Math.min(5, Number(path.slice(1)) || 0)) : 0,
  };
};

/** How a piece sits in the hand (rotation of its pivot), by kind: upright staffs and bows, a levelled crossbow, tilted blades. */
const UPRIGHT: ReadonlySet<GearBaseId> = new Set(['staff', 'quarterstaff', 'longbow', 'shortbow']);
const heldPose = (base: GearBaseId, off: boolean): [number, number, number] => {
  if (base === 'heavyCrossbow') return [Math.PI / 2 - 0.15, 0, 0];
  if (UPRIGHT.has(base)) return [0.08, 0.2, 0];
  if (base === 'shield') return [0, -0.35, 0];
  if (GEAR_BASES[base].slot === 'offhand' && base !== 'offAxe') return [0.25, 0, 0];
  return off ? [0.35, 0, 0.12] : [0.35, 0, -0.12];
};

const clear = (g: THREE.Object3D): void => {
  for (const c of [...g.children]) g.remove(c);
};

/** Hangs a held piece on a hand pivot; a spectre's pieces turn spectral like the rest of it. */
const hold = (hand: THREE.Object3D, base: GearBaseId, rarity: GearRarity, off: boolean, ghost: string | undefined): void => {
  const p = gearHeld(base, rarity);
  p.rotation.set(...heldPose(base, off));
  if (base === 'shield') p.position.set(-0.06, 0.02, 0.02);
  // An orb rides above the hand, not down by the robe.
  if (base === 'orb') p.position.y = 0.14;
  if (ghost) {
    const mat = spectral(ghost);
    p.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).material = mat;
    });
  }
  hand.add(p);
};

/**
 * Puts the look's pieces in the hero's hands: the main hand's piece (unless it is the class's usual weapon
 * at common, which the class draws better, or a unique piece), the off-hand's, and nothing in the other
 * hand with a two-handed weapon. With no off-hand piece the class keeps what it always held there (the
 * warrior's shield), so a hero who wears only an older weapon looks as it did.
 */
const dressHands = (rig: HeroRig, cls: HeroClass, look: Look, ghost: string | undefined): void => {
  // The ranger draws its bow with the left hand; its right hand nocks the arrow.
  const mainHand = cls === 'ranger' ? rig.offhand : rig.weapon;
  const offHand = cls === 'ranger' ? null : rig.offhand;
  const main = look.main;
  if (main && main !== 'u' && (main !== CLASS_WEAPON[cls] || look.rarity !== 'common')) {
    clear(mainHand);
    hold(mainHand, main, look.rarity, false, ghost);
  }
  const twoHanded = !!main && main !== 'u' && GEAR_BASES[main].hands === 2;
  if (!offHand) return;
  if (twoHanded) clear(offHand);
  else if (look.off && look.off !== 'u' && look.off !== 'quiver') {
    clear(offHand);
    hold(offHand, look.off, look.offRarity, true, ghost);
  }
};

/* ---------- head, chest and feet (docs/EQUIPO.md §2 «Peso», §10) ---------- */

/**
 * What a worn piece is made of, in figure scale. Every material is a shared, cached one (the cards' own,
 * models/gear.ts), so a dressed hero adds no material; a spectre's pieces are all its one spectral material.
 */
const TAU = Math.PI * 2;
const DARK = std('#0e0b0a');
const BROW = metal('#9aa0a8', { roughness: 0.3 });
const JEWEL = std('#9fb4c8', { roughness: 0.2 });
/** The warrior's crest red, for the plate helm's crest. */
const CREST = cloth('#7a2328');

/** Mail worn on the figure: darker and duller than plate, its grain standing for the rings. */
const MAIL = metal('#79808a', { roughness: 0.78, bumpScale: 0.08 });
const CLOTH_BOOT = std('#4a4458', { roughness: 0.95, bumpScale: 0.03 });
const CLOTH_WRAP = std('#2e2a36', { roughness: 1 });
/** The absent's cloak worn: its watch grey, one-sided (a dressed hero adds nothing two-sided; the hem is seen outside). */
const WATCH = cloth('#6b7078');

/**
 * Hair for a head that only ever wore a hood or helm (the circlet shows it); the cleric and the barbarian
 * have their own. The mage's matches its beard.
 */
const HAIR: Partial<Record<HeroClass, THREE.Material>> = {
  warrior: std('#3b2a1f', { roughness: 0.9 }),
  artificer: std('#4a3424', { roughness: 0.9 }),
  mage: std('#c9c6bd', { roughness: 1 }),
  ranger: std('#4a3424', { roughness: 0.9 }),
  paladin: std('#2e2622', { roughness: 0.9 }),
  druid: std('#3a2e22', { roughness: 0.9 }),
};

/** The frame inside a pivot (figure coordinates follow the limb). */
const absOf = (pivot: THREE.Object3D): THREE.Object3D => pivot.children[0] ?? pivot;

const byName = (root: THREE.Object3D, name: string): THREE.Object3D[] => {
  const out: THREE.Object3D[] = [];
  root.traverse((o) => {
    if (o.name === name) out.push(o);
  });
  return out;
};

/** Adds a mesh in a shared material, or in the spectre's. */
type Put = (geo: THREE.BufferGeometry, mat: THREE.Material, parent: THREE.Object3D) => THREE.Mesh;
const putter = (ghost: string | undefined): { put: Put; mat: (m: THREE.Material) => THREE.Material } => {
  const g = ghost ? spectral(ghost) : null;
  return { put: (geo, mat, parent) => M(geo, g ?? mat, parent), mat: (m) => g ?? m };
};

/** A cowl with its face open toward +Z (hood and coif), in head frame. */
const cowl = (h: THREE.Object3D, put: Put, mat: THREE.Material, r: number, open: number): THREE.Mesh => {
  const c = put(new THREE.SphereGeometry(r, 10, 6, Math.PI / 2 + open, TAU - open * 2, 0, Math.PI * 0.8), mat, h);
  c.position.set(0, 1.645, -0.01);
  c.scale.set(1, 1.1, 1.06);
  return c;
};

const dressHead = (rig: HeroRig, cls: HeroClass, base: GearBaseId, ghost: string | undefined): void => {
  const h = absOf(rig.head);
  const { put, mat } = putter(ghost);
  for (const g of byName(h, 'headgear')) g.removeFromParent();
  if (base !== 'circlet') for (const k of byName(h, 'topknot')) k.removeFromParent();
  if (base === 'circlet') {
    const hair = HAIR[cls];
    if (hair) {
      const cap = put(new THREE.SphereGeometry(0.128, 9, 4, 0, TAU, 0, Math.PI / 2.1), hair, h);
      cap.position.set(0, 1.64, -0.012);
      cap.rotation.x = -0.32;
    }
    // A cloth band round the brow with a steel brow plate and a pale stone, two tails at the back.
    const band = G(h, 0, 1.685, 0);
    band.rotation.x = 0.12;
    ring(band, 0.127, 0.017, mat(CLOTH_B), 0, 3, 12);
    const plate = put(new THREE.OctahedronGeometry(0.04), BROW, band);
    plate.position.set(0, 0.012, 0.13);
    plate.scale.set(1.1, 1.3, 0.35);
    put(new THREE.OctahedronGeometry(0.019), JEWEL, band).position.set(0, 0.014, 0.143);
    for (const k of [-1, 1]) limb(band, V(k * 0.03, -0.005, -0.12), V(k * 0.07, -0.16, -0.16), 0.014, 0.008, mat(CLOTH_B), 4);
    return;
  }
  if (base === 'hood' || base === 'mossHood') {
    // Leather, the point falling back, a dark mantle round the throat.
    cowl(h, put, HIDE, 0.152, 0.78);
    const tip = put(new THREE.ConeGeometry(0.06, 0.2, 7), HIDE, h);
    tip.position.set(0, 1.74, -0.15);
    tip.rotation.x = -2.0;
    const m = put(new THREE.CylinderGeometry(0.13, 0.22, 0.1, 10, 1, true), HIDE, h);
    m.position.y = 1.5;
    m.scale.z = 0.9;
    if (base === 'mossHood') {
      // The moss hood (docs/SABERES.md §4.4): moss round the face's rim, over the mantle and a tuft at the point.
      for (let i = 0; i < 7; i++) {
        const a = -1.1 + (i / 6) * 2.2;
        const t = put(new THREE.DodecahedronGeometry(0.03, 0), i % 2 ? MOSS : MOSS_DK, h);
        t.position.set(Math.sin(a) * 0.13, 1.66 + Math.cos(a) * 0.12, 0.1 + Math.cos(a) * 0.03);
      }
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * TAU + 0.3;
        const t = put(new THREE.DodecahedronGeometry(0.035, 0), i % 2 ? MOSS_DK : MOSS, h);
        t.position.set(Math.sin(a) * 0.2, 1.47, Math.cos(a) * 0.18);
        t.scale.y = 0.6;
      }
      put(new THREE.DodecahedronGeometry(0.04, 0), MOSS, h).position.set(0, 1.79, -0.22);
    }
    return;
  }
  if (base === 'coif') {
    // Mail over the head and down onto the shoulders, ring rows across it, a leather edge at the hem.
    cowl(h, put, MAIL, 0.152, 0.6);
    const m = put(new THREE.CylinderGeometry(0.13, 0.23, 0.12, 10, 1, true), MAIL, h);
    m.position.y = 1.5;
    m.scale.z = 0.9;
    for (const [y, r] of [[1.7, 0.14], [1.47, 0.205]] as const) ring(h, r, 0.011, mat(DSTEEL), y, 3, 10).scale.y = 0.95;
    return;
  }
  // helm: a steel bascinet with cheek guards, a dark eye slit, a ridge on top and a rim at the neck.
  const dome = put(new THREE.SphereGeometry(0.148, 10, 4, 0, TAU, 0, Math.PI / 2), PLATE, h);
  dome.position.y = 1.655;
  dome.scale.set(1, 1.12, 1.07);
  const cheek = put(new THREE.CylinderGeometry(0.148, 0.14, 0.15, 10, 1, true), PLATE, h);
  cheek.position.y = 1.58;
  cheek.scale.z = 1.07;
  put(new THREE.BoxGeometry(0.2, 0.026, 0.05), DARK, h).position.set(0, 1.635, 0.14);
  put(new THREE.BoxGeometry(0.03, 0.13, 0.03), PLATE, h).position.set(0, 1.575, 0.158);
  // A short red crest along the top.
  const crest = put(new THREE.BoxGeometry(0.035, 0.085, 0.24), CREST, h);
  crest.position.set(0, 1.81, -0.02);
};

/** The class's chest pieces that a worn chest covers: its torso, shoulders and the layers worn over them. */
const chestParts = (b: THREE.Object3D): THREE.Mesh[] => {
  const torso = b.children.find((o) => o.name === 'torso') as THREE.Mesh | undefined;
  const skin = torso?.material;
  return b.children.filter((o): o is THREE.Mesh => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return false;
    if (m.name === 'torso' || m.name === 'shoulder') return true;
    const y = m.position.y;
    if (y < 1.0 || y > 1.46) return false;
    if (m.material === skin) return true;
    // A jerkin, capelet or mantle over the torso (a cylinder round it).
    const p = (m.geometry as THREE.CylinderGeometry).parameters;
    return m.geometry.type === 'CylinderGeometry' && Math.max(p.radiusTop, p.radiusBottom) >= 0.2;
  });
};

/** How far the class's chest reaches out at height y (its torso and the cylinders worn over it), for rings round it. */
const chestRadius = (parts: THREE.Mesh[], y: number): number => {
  let r = 0.16 + 0.05 * (y - 0.97) / 0.5;
  for (const m of parts) {
    if (m.geometry.type !== 'CylinderGeometry' || m.name === 'shoulder') continue;
    const p = (m.geometry as THREE.CylinderGeometry).parameters;
    if (p.thetaLength < TAU - 0.01) continue;
    const u = (y - m.position.y) / p.height + 0.5;
    if (u < 0 || u > 1) continue;
    r = Math.max(r, (p.radiusBottom + (p.radiusTop - p.radiusBottom) * u) * Math.max(m.scale.x, m.scale.z));
  }
  return r;
};

/**
 * A class's own long robe (mage, cleric, druid): the open cone or cylinder hanging from the hips to the ground,
 * the widest one when it wears two (the cleric's outer robe), and what is painted its colour (the cleric's folds).
 */
const longRobe = (fig: THREE.Object3D): THREE.Mesh[] => {
  let outer: THREE.Mesh | undefined;
  let widest = 0;
  for (const o of fig.children) {
    const m = o as THREE.Mesh;
    if (!m.isMesh || (m.geometry.type !== 'ConeGeometry' && m.geometry.type !== 'CylinderGeometry')) continue;
    const p = (m.geometry as THREE.CylinderGeometry).parameters;
    if (!p.openEnded || p.height < 0.8) continue;
    const r = m.geometry.type === 'ConeGeometry' ? (p as unknown as { radius: number }).radius : p.radiusBottom;
    if (r > widest) {
      widest = r;
      outer = m;
    }
  }
  if (!outer) return [];
  const hex = (outer.material as THREE.MeshStandardMaterial).color.getHex();
  return fig.children.filter((o): o is THREE.Mesh => {
    const m = o as THREE.Mesh;
    return !!m.isMesh && (m === outer || (m.material as THREE.MeshStandardMaterial).color?.getHex() === hex);
  });
};

/** A skirt hanging from the hips: in the figure, so it doesn't swing with the legs. */
const skirt = (fig: THREE.Object3D, put: Put, mat: THREE.Material, top: number, bottom: number, y0: number, y1: number): void => {
  put(new THREE.CylinderGeometry(top, bottom, y1 - y0, 12, 1, true), mat, fig).position.y = (y0 + y1) / 2;
};

const CHEST_MAT: Partial<Record<GearBaseId, THREE.Material>> = { robe: CLOTH_A, jerkin: HIDE, hauberk: MAIL, breastplate: PLATE };

const dressChest = (rig: HeroRig, base: GearBaseId, ghost: string | undefined): void => {
  const b = absOf(rig.body);
  const fig = rig.figure;
  const { put, mat } = putter(ghost);
  const cover = CHEST_MAT[base];
  const parts = chestParts(b);
  // A class already in a long robe (mage, cleric, druid) keeps its own colour under a cloth robe: grey would
  // wash out who it is. The sash and the collar say it is worn.
  const own = base === 'robe' ? longRobe(fig) : [];
  if (!ghost && cover && own.length === 0) for (const m of parts) m.material = cover;
  if (base === 'robe') {
    // The others get a tunic skirt to the knee; both add a red sash and a soft collar.
    if (own.length === 0) {
      skirt(fig, put, CLOTH_A, 0.175, 0.28, 0.46, 0.96);
      const end = put(new THREE.BoxGeometry(0.05, 0.3, 0.015), CLOTH_TRIM, fig);
      end.position.set(0.08, 0.83, 0.235);
      end.rotation.x = -0.25;
    }
    ring(b, 0.178, 0.026, mat(CLOTH_TRIM), 0.985, 3, 12);
    ring(b, 0.13, 0.035, mat(CLOTH_B), 1.47, 3, 12);
    return;
  }
  if (base === 'absentCloak') {
    // The absent's cloak (docs/SABERES.md §4.4): a long watch-grey cloak from the shoulders to the heels, open at the
    // front, its folds and dark hem, the hood lying back, the clasp with the Vigil's pale eye.
    const cl = put(new THREE.CylinderGeometry(0.2, 0.34, 1.12, 12, 1, true, 0.9, TAU - 1.8), WATCH, fig);
    cl.position.set(0, 0.86, -0.02);
    cl.scale.z = 0.85;
    for (let i = 0; i < 4; i++) {
      const a = 1.4 + i * 0.95;
      const fold = put(new THREE.CylinderGeometry(0.015, 0.03, 1.05, 5), WATCH_DK, fig);
      fold.position.set(Math.sin(a) * 0.27, 0.86, Math.cos(a) * 0.23 - 0.02);
    }
    const hood = put(new THREE.SphereGeometry(0.13, 8, 5, 0, TAU, 0, Math.PI * 0.6), WATCH, b);
    hood.position.set(0, 1.46, -0.14);
    hood.rotation.x = -1.9;
    ring(b, 0.13, 0.03, mat(WATCH_DK), 1.47, 3, 12);
    const clasp = put(new THREE.CylinderGeometry(0.035, 0.035, 0.015, 8), DSTEEL, b);
    clasp.rotation.x = Math.PI / 2;
    clasp.position.set(0, 1.42, 0.15);
    put(new THREE.SphereGeometry(0.014, 6, 4), VIGIL_EYE, b).position.set(0, 1.42, 0.16);
    return;
  }
  if (base === 'jerkin') {
    // Leather flaps over the hips, domed shoulder caps and a dark collar.
    for (let i = 0; i < 5; i++) {
      const a = (i - 2) * 0.62;
      const fl = G(fig, Math.sin(a) * 0.19, 0.84, Math.cos(a) * 0.19);
      fl.rotation.y = a;
      const p = put(new THREE.BoxGeometry(0.11, 0.2, 0.02), HIDE_D, fl);
      p.rotation.x = -0.18;
    }
    for (const k of [-1, 1]) {
      const cap = put(new THREE.SphereGeometry(0.1, 8, 3, 0, TAU, 0, Math.PI / 2), HIDE_D, b);
      cap.position.set(k * 0.235, 1.43, 0);
      cap.rotation.z = -k * 0.6;
      cap.scale.set(1.05, 0.6, 1.1);
    }
    ring(b, 0.125, 0.03, mat(HIDE_D), 1.47, 3, 12);
    return;
  }
  if (base === 'hauberk') {
    // A mail skirt below the belt with ring rows, and mail over the shoulders.
    skirt(fig, put, MAIL, 0.18, 0.26, 0.68, 0.96);
    for (const [y, r] of [[0.82, 0.225], [0.7, 0.257]] as const) ring(fig, r, 0.008, mat(DSTEEL), y, 3, 12);
    for (const y of [1.14, 1.3]) ring(b, chestRadius(parts, y) + 0.004, 0.008, mat(DSTEEL), y, 3, 12);
    const mantle = put(new THREE.CylinderGeometry(0.15, 0.25, 0.12, 10, 1, true), MAIL, b);
    mantle.position.y = 1.44;
    return;
  }
  // breastplate: plate pauldrons (in place of the class's own), a ridge and a fauld of three tassets.
  for (const g of [...b.children]) {
    if (!(g as THREE.Group).isGroup) continue;
    if (Math.abs(Math.abs(g.position.x) - 0.255) < 0.03 && Math.abs(g.position.y - 1.43) < 0.03) g.removeFromParent();
  }
  for (const k of [-1, 1]) {
    const pa = G(b, k * 0.25, 1.43, 0);
    pa.rotation.z = -k * 0.62;
    pa.scale.setScalar(1.2);
    put(new THREE.SphereGeometry(0.105, 8, 3, 0, TAU, 0, Math.PI / 2), PLATE, pa).scale.set(1.08, 0.5, 1.15);
    const lame = put(new THREE.SphereGeometry(0.1, 8, 1, 0, TAU, Math.PI / 2 - 0.5, 0.5), PLATE, pa);
    lame.position.y = -0.05;
    lame.scale.set(1.14, 0.9, 1.22);
  }
  for (const k of [-1, 0, 1]) {
    const ts = put(new THREE.BoxGeometry(0.12, 0.18, 0.024), PLATE, b);
    ts.position.set(k * 0.12, 0.83, 0.16 - Math.abs(k) * 0.04);
    ts.rotation.set(-0.15, k * 0.5, 0);
  }
};

const BOOT_MAT: Partial<Record<GearBaseId, THREE.Material>> = { clothBoots: CLOTH_BOOT, leatherBoots: HIDE, mailBoots: MAIL, greaves: PLATE };

const dressFeet = (rig: HeroRig, base: GearBaseId, ghost: string | undefined): void => {
  const { put, mat } = putter(ghost);
  const boot = BOOT_MAT[base] ?? HIDE;
  for (const shin of [rig.shinL, rig.shinR]) {
    if (!shin) continue;
    const s = absOf(shin);
    const k = shin === rig.shinL ? -1 : 1;
    for (const m of s.children) if (m.name === 'boot' && !ghost) (m as THREE.Mesh).material = boot;
    const x = k * 0.091;
    if (base === 'greaves') {
      // A plate greave over the shin, a ridge down its front and a knee cop.
      limb(s, V(x, 0.12, 0.005), V(k * 0.095, 0.46, 0.005), 0.074, 0.08, mat(PLATE), 6);
      put(new THREE.BoxGeometry(0.025, 0.3, 0.03), PLATE, s).position.set(x, 0.3, 0.075);
      const cop = put(new THREE.SphereGeometry(0.076, 6, 4), PLATE, s);
      cop.position.set(k * 0.095, 0.5, 0.045);
      cop.scale.set(1, 0.9, 0.75);
      continue;
    }
    // A boot shaft up the shin and its cuff.
    const top = base === 'clothBoots' ? 0.27 : 0.31;
    limb(s, V(x, 0.12, 0.005), V(x, top, 0.005), 0.071, 0.074, mat(boot), 6);
    const cuff = base === 'clothBoots' ? CLOTH_WRAP : base === 'leatherBoots' ? HIDE_D : LEATHER_BELT;
    ring(s, 0.077, 0.015, mat(cuff), top, 3, 8).position.set(x, top, 0.005);
    if (base === 'mailBoots') ring(s, 0.075, 0.007, mat(DSTEEL), 0.2, 3, 8).position.set(x, 0.2, 0.005);
    else if (base === 'clothBoots') ring(s, 0.074, 0.009, mat(CLOTH_WRAP), 0.18, 3, 8).position.set(x, 0.18, 0.005);
  }
};

/**
 * The path's pieces earned so far (docs/sendas/*.md §10), one a chapter. The warrior's (docs/sendas/guerrero.md):
 * 1, the Company's red ribbon tied round the shield arm; 2, the bridge's ring at the belt, an iron ring with a knot of
 * rope; 3, the sooted pauldron on the shield's shoulder, black with soot that never comes off (a heavy plate over the
 * class's own, a scorched edge, the soot running down onto the arm). Small, but they read on the board (the red
 * against the armour, the black against the steel); 4, the iron wedge from the avalanche at the other hip, a strip of
 * red tied through it; 5, the whole look: the Company's white cloak with its red edge and its plume of snow, and the
 * Company's mark on the shield. Other classes' pieces come with their paths.
 */
const dressPath = (rig: HeroRig, cls: HeroClass, n: number, ghost: string | undefined): void => {
  if (cls === 'paladin') return dressPaladinPath(rig, n, ghost);
  if (cls === 'artificer') return dressArtificerPath(rig, n, ghost);
  if (cls === 'barbarian') return dressBarbarianPath(rig, n, ghost);
  if (cls === 'cleric') return dressClericPath(rig, n, ghost);
  if (cls === 'druid') return dressDruidPath(rig, n, ghost);
  if (cls === 'ranger') return dressRangerPath(rig, n, ghost);
  if (cls === 'mage') return dressMagePath(rig, n, ghost);
  if (cls !== 'warrior') return;
  const { put, mat } = putter(ghost);
  const red = cloth('#b8322a');
  if (n >= 1) {
    // The ribbon round the upper arm (the shield's side), its two ends hanging.
    const arm = absOf(rig.armL);
    ring(arm, 0.066, 0.02, mat(red), 1.27, 4, 12).position.set(-0.255, 1.27, 0);
    for (const k of [-1, 1]) {
      const end = put(new THREE.BoxGeometry(0.03, 0.12, 0.01), red, arm);
      end.position.set(-0.32, 1.22, 0.02 * k);
      end.rotation.z = 0.35 + k * 0.12;
    }
  }
  if (n >= 2) {
    // The bridge's ring hanging at the right hip, a knot of rope through it.
    const b = absOf(rig.body);
    const iron = mat(std('#4a4f57', { metalness: 0.6, roughness: 0.45 }));
    const r = M(new THREE.TorusGeometry(0.075, 0.016, 5, 14), iron, b);
    r.position.set(0.21, 0.84, 0.1);
    r.rotation.y = 0.9;
    const rope = mat(std('#8a7656', { roughness: 1 }));
    M(new THREE.SphereGeometry(0.032, 6, 5), rope, b).position.set(0.2, 0.92, 0.1);
    limb(b, V(0.2, 0.92, 0.1), V(0.24, 0.79, 0.13), 0.012, 0.01, rope, 4);
  }
  if (n >= 3) {
    // The pauldron over the left shoulder: three lames of plate gone black, the soot fading to grey at the rim.
    const b = absOf(rig.body);
    const soot = mat(std('#17171a', { metalness: 0.35, roughness: 0.75, flatShading: true }));
    const ash = mat(std('#45474d', { metalness: 0.3, roughness: 0.85, flatShading: true }));
    for (let i = 0; i < 3; i++) {
      const lame = M(new THREE.SphereGeometry(0.125 - i * 0.012, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.5), i === 2 ? ash : soot, b);
      lame.position.set(-0.235 - i * 0.012, 1.46 - i * 0.055, 0);
      lame.scale.set(1.12, 0.62, 1.15);
      lame.rotation.z = 0.42 + i * 0.12;
    }
    // A smear of soot down the upper arm.
    put(new THREE.BoxGeometry(0.05, 0.12, 0.012), std('#202024', { roughness: 1 }), absOf(rig.armL)).position.set(-0.27, 1.25, 0.06);
  }
  if (n >= 4) {
    // The iron wedge from the avalanche at the left hip (the ring hangs on the right): dark iron, point down, a strip of
    // the Company's red tied through its eye.
    const b = absOf(rig.body);
    const iron = mat(std('#3b3f46', { metalness: 0.55, roughness: 0.5, flatShading: true }));
    const w = M(new THREE.ConeGeometry(0.05, 0.24, 4), iron, b);
    w.position.set(-0.21, 0.78, 0.1);
    w.rotation.set(0, Math.PI / 4, Math.PI);
    w.scale.z = 0.45;
    M(new THREE.BoxGeometry(0.075, 0.05, 0.04), iron, b).position.set(-0.21, 0.91, 0.1);
    put(new THREE.BoxGeometry(0.025, 0.1, 0.012), red, b).position.set(-0.19, 0.86, 0.13);
  }
  if (n >= 5) {
    // The whole look (chapter 5): the Company's cloak, white as snow with a red edge, over whatever the class wore;
    // a short plume of blown snow off its hem (one translucent ribbon, no particles: it reads as the cloak's own wake);
    // and the Company's mark on the shield, a line with eleven notches under a peak, in red on a pale field.
    const b = absOf(rig.body);
    const white = mat(std('#e7ecf2', { roughness: 0.95, side: THREE.DoubleSide }));
    const len = 1.15;
    const arc = 2.1;
    if (rig.cape) rig.cape.visible = false;
    const cp = G(b, 0, 1.46, 0);
    M(new THREE.CylinderGeometry(0.25, 0.45, len, 14, 1, true, Math.PI - arc / 2, arc), white, cp).position.y = -len / 2;
    const hem = M(new THREE.CylinderGeometry(0.452, 0.455, 0.07, 14, 1, true, Math.PI - arc / 2, arc), mat(std('#b8322a', { roughness: 1, side: THREE.DoubleSide })), cp);
    hem.position.y = -len + 0.035;
    // Its clasp at the throat: an iron ring with a red knot.
    M(new THREE.TorusGeometry(0.035, 0.01, 4, 10), mat(std('#3b3f46', { metalness: 0.5, roughness: 0.5 })), b).position.set(0, 1.44, 0.14);
    if (!ghost) {
      const plume = M(
        new THREE.PlaneGeometry(0.85, 0.42),
        new THREE.MeshBasicMaterial({ color: '#eef4fb', transparent: true, opacity: 0.32, depthWrite: false, side: THREE.DoubleSide, alphaMap: plumeTexture() }),
        b,
      );
      plume.position.set(0, 0.42, -0.62);
      plume.rotation.set(-1.25, 0, 0);
    }
    // The mark on the shield (the off hand): a pale field, the line and its notches, the peak over them.
    const off = rig.offhand;
    const mark = G(off, -0.06, 0.04, 0.09);
    mark.rotation.set(0, -0.35, 0);
    put(new THREE.BoxGeometry(0.2, 0.2, 0.008), std('#d9dde2', { roughness: 0.9 }), mark);
    put(new THREE.BoxGeometry(0.17, 0.016, 0.01), red, mark).position.set(0, -0.03, 0.006);
    for (let i = 0; i < 11; i++) put(new THREE.BoxGeometry(0.006, 0.03, 0.01), red, mark).position.set(-0.075 + i * 0.015, -0.045, 0.007);
    for (const k of [-1, 1]) {
      const side = put(new THREE.BoxGeometry(0.075, 0.014, 0.01), red, mark);
      side.position.set(k * 0.027, 0.035, 0.007);
      side.rotation.z = -k * 0.75;
    }
  }
};

/**
 * The barbarian's pieces (docs/sendas/barbaro.md §10): 1, Bera's hunting stripe: a band of red mud drawn across the eyes
 * from temple to temple, a little proud of the face and lit a touch so it reads on the board at night; 2, the clan's
 * leather bracer with the horn burned into it, on the left forearm; 3, the clan's waxed wool cloak, grey with the red
 * edge; 4, Sigrun's hunting paint on the arms; 5, Sigrun's wolf pelt over the shoulders (sigrunPelt), and the paint
 * becomes its own material that lights while there is prey (preyPaint).
 */
/** The clan of the Horn's red (models/caza.ts CLAN). */
const CLAN_RED = '#a43a2c';

const dressBarbarianPath = (rig: HeroRig, n: number, ghost: string | undefined): void => {
  const { put, mat } = putter(ghost);
  if (n >= 1) {
    const h = absOf(rig.head);
    const mud = mat(std('#b3392a', { roughness: 0.95, emissive: '#5a120a', emissiveIntensity: 0.55 }));
    const band = put(new THREE.CylinderGeometry(0.152, 0.152, 0.042, 18, 1, true, -1.35, 2.7), mud, h);
    band.position.set(0, 1.662, 0.004);
    // Its ends, smeared down at the temples.
    for (const k of [-1, 1]) {
      const end = put(new THREE.BoxGeometry(0.02, 0.07, 0.03), mud, h);
      end.position.set(k * 0.148, 1.64, 0.035);
      end.rotation.z = k * 0.25;
    }
  }
  // 2, the clan's leather bracer on the left forearm: dark hide laced over the wrist, the horn burned pale into it.
  if (n >= 2 && rig.foreL) {
    const f = absOf(rig.foreL);
    const hide = mat(std('#3b2b22', { roughness: 0.9 }));
    const cuff = put(new THREE.CylinderGeometry(0.062, 0.056, 0.17, 10), hide, f);
    cuff.position.set(-0.278, 0.99, 0.1);
    cuff.rotation.x = 0.3;
    for (const dy of [-0.06, 0.06]) {
      const lace = put(new THREE.TorusGeometry(0.06, 0.008, 4, 12), mat(std('#6a5a48', { roughness: 0.95 })), f);
      lace.position.set(-0.278, 0.99 + dy, 0.1 + dy * 0.3);
      lace.rotation.x = Math.PI / 2 + 0.3;
    }
    // The horn, burned in: a pale curved mark on the outside of the arm, lit a touch to read on the board.
    const horn = put(new THREE.TorusGeometry(0.03, 0.007, 4, 10, Math.PI * 1.1), mat(std('#d9cfc4', { roughness: 0.7, emissive: '#3a2a20', emissiveIntensity: 0.4 })), f);
    horn.position.set(-0.338, 0.99, 0.1);
    horn.rotation.set(0.3, Math.PI / 2, 0.4);
  }
  // 3, the clan's waxed wool cloak: grey, a little sheen of the wax on it, the clan's red at the hem, a thick roll of
  // wool at the shoulders and a bone pin at the throat (shorter than a knight's: to the back of the knees, for rowing).
  if (n >= 3 && rig.body) {
    const b = absOf(rig.body);
    const wool = mat(std('#6b7077', { roughness: 0.62, metalness: 0.04, side: THREE.DoubleSide }));
    const len = 0.92;
    const arc = 2.3;
    if (rig.cape) rig.cape.visible = false;
    const cp = G(b, 0, 1.47, -0.01);
    put(new THREE.CylinderGeometry(0.26, 0.42, len, 14, 1, true, Math.PI - arc / 2, arc), wool, cp).position.y = -len / 2;
    const hem = put(new THREE.CylinderGeometry(0.422, 0.425, 0.08, 14, 1, true, Math.PI - arc / 2, arc), mat(std(CLAN_RED, { roughness: 0.9, side: THREE.DoubleSide })), cp);
    hem.position.y = -len + 0.04;
    const roll = put(new THREE.TorusGeometry(0.2, 0.055, 6, 16, Math.PI * 1.35), mat(std('#5e636a', { roughness: 0.85 })), b);
    roll.position.set(0, 1.47, -0.02);
    roll.rotation.set(Math.PI / 2, 0, Math.PI * 0.82);
    const pin = put(new THREE.CylinderGeometry(0.012, 0.008, 0.12, 5), mat(std('#d7d9d6', { roughness: 0.7, emissive: '#2a2622', emissiveIntensity: 0.3 })), b);
    pin.position.set(0.05, 1.45, 0.16);
    pin.rotation.z = 1.1;
  }
  // 4, Sigrun's hunting paint (the diary's): three bands of dark red down each arm, the middle one a zigzag of claw
  // marks, from the shoulder to the wrist (over the bracer's edge on the left); lit a touch so it reads by night.
  if (n >= 4) {
    // (With the whole look the paint is its own material, never shared: the fight lights it while there is prey.)
    const paint = n >= 5 && !ghost ? preyPaint() : mat(std('#8a2418', { roughness: 0.9, emissive: '#4a0c06', emissiveIntensity: 0.6 }));
    for (const k of [-1, 1]) {
      const fore = k < 0 ? rig.foreL : rig.foreR;
      const up = absOf(k < 0 ? rig.armL : rig.armR);
      for (const dy of [0, -0.09]) {
        const band = put(new THREE.CylinderGeometry(0.066, 0.066, 0.028, 12, 1, true), paint, up);
        band.position.set(k * 0.27, 1.27 + dy, 0.04);
      }
      for (let i = 0; i < 3; i++) {
        const claw = put(new THREE.BoxGeometry(0.014, 0.1, 0.012), paint, up);
        claw.position.set(k * 0.33, 1.23, 0.0 + (i - 1) * 0.03);
        claw.rotation.x = 0.15;
      }
      if (!fore) continue;
      const f = absOf(fore);
      const y0 = k < 0 ? 1.1 : 0.99;
      for (const dy of k < 0 ? [0] : [0.07, -0.02, -0.08]) {
        const band = put(new THREE.CylinderGeometry(0.06, 0.057, 0.026, 12, 1, true), paint, f);
        band.position.set(k * 0.278, y0 + dy, 0.1 + dy * 0.3);
        band.rotation.x = 0.3;
      }
    }
  }
  if (n >= 5 && rig.body) sigrunPelt(rig, put, mat);
};

/** The name of the barbarian's paint once the path is done: the fight finds it in the baked model and lights it. */
export const PREY_PAINT = 'prey-paint';

/**
 * Sigrun's paint with the whole look (piece 5): a material of its own (`userData.keep`: baking never merges it into a
 * shared one), dull red at rest. engine/world.ts turns its colour toward the dragon's red while the barbarian has prey
 * (no new light: only the material's colour and its glow).
 */
const preyPaint = (): THREE.MeshStandardMaterial => {
  const m = new THREE.MeshStandardMaterial({ color: '#8a2418', roughness: 0.9, emissive: '#4a0c06', emissiveIntensity: 0.6 });
  m.name = PREY_PAINT;
  m.userData.keep = true;
  return m;
};

/**
 * 5, Sigrun's wolf pelt (docs/sendas/barbaro.md §10): the skin of a real grey wolf of the clans (not the Grey Wolf's: of
 * that one nothing is left), the one Bera sewed for her before the Hunt. Over the shoulders and down the back, a dark
 * saddle along its spine and a ragged hem; its head on the left shoulder looking forward (over the spiked pauldron of
 * tier III: on the right the axe and its glow hid it), ears up; its forepaws crossed on the chest and knotted with the clan's red wool, Bera's red
 * stitches along its edge. Cold greys only, so the keep's fire never turns it brown or cream.
 */
const sigrunPelt = (rig: HeroRig, put: Put, mat: (m: THREE.Material) => THREE.Material): void => {
  const b = absOf(rig.body!);
  const grey = mat(std('#7b8087', { roughness: 1, bumpScale: 0.05 }));
  const dark = mat(std('#43474d', { roughness: 1, bumpScale: 0.05 }));
  const pale = mat(std('#aab0b8', { roughness: 1, bumpScale: 0.03 }));
  const nose = mat(std('#16171a', { roughness: 0.45 }));
  const red = mat(std(CLAN_RED, { roughness: 0.9, emissive: '#3a0c06', emissiveIntensity: 0.4 }));
  // The skin over the back, from the shoulders to the small of the back (the cloak's red hem shows under it).
  const len = 0.5;
  const arc = 3.2;
  const back = G(b, 0, 1.5, -0.02);
  put(new THREE.CylinderGeometry(0.27, 0.37, len, 16, 1, true, Math.PI - arc / 2, arc), grey, back).position.y = -len / 2;
  // The saddle: a broad dark strip down the spine, and the fur standing in rows over the skin (so it reads as fur, not cloth).
  const saddle = put(new THREE.CylinderGeometry(0.279, 0.377, len * 0.95, 8, 1, true, Math.PI - 0.5, 1.0), dark, back);
  saddle.position.y = -len / 2;
  for (let row = 0; row < 3; row++)
    for (let i = 0; i < 6; i++) {
      const a = Math.PI + (i - 2.5) * 0.42 + (row % 2 ? 0.2 : -0.1);
      const y = 1.4 - row * 0.15 - (i % 2) * 0.04;
      // Half sunk in the skin (its radius there), each clump a little different, lying down along the back.
      const r = 0.27 + ((1.5 - y) / len) * 0.1 + 0.004;
      const tuft = put(new THREE.SphereGeometry(0.05 + ((i + row) % 3) * 0.012, 6, 4), Math.abs(i - 2.5) < 1 ? dark : grey, b);
      tuft.position.set(Math.sin(a) * r, y, Math.cos(a) * r - 0.02);
      tuft.rotation.set(0, a, ((i * 7 + row * 3) % 5) * 0.12 - 0.24);
      tuft.scale.set(0.8, 1.7, 0.32);
    }
  // Over each shoulder, a thick fold of fur.
  for (const k of [-1, 1]) {
    const cap = put(new THREE.SphereGeometry(0.145, 10, 6), grey, b);
    cap.position.set(k * 0.25, 1.48, -0.02);
    cap.scale.set(1.05, 0.6, 1.15);
  }
  // The ragged hem.
  for (let i = 0; i < 11; i++) {
    const a = Math.PI - arc / 2 + 0.15 + (i / 10) * (arc - 0.3);
    const tuft = put(new THREE.ConeGeometry(0.05, 0.15, 4), i % 3 === 1 ? dark : grey, b);
    tuft.position.set(Math.sin(a) * 0.37, 1.5 - len - 0.04, Math.cos(a) * 0.37 - 0.02);
    tuft.rotation.set(Math.PI + Math.cos(a) * 0.2, 0, -Math.sin(a) * 0.2);
  }
  // The hind legs' skin hanging at the sides of the back, a pale paw at each end, and the tail down the middle.
  for (const k of [-1, 1]) {
    const leg = put(new THREE.CylinderGeometry(0.04, 0.05, 0.34, 6), grey, b);
    leg.position.set(k * 0.24, 0.86, -0.22);
    leg.rotation.set(-0.1, 0, k * 0.12);
    const paw = put(new THREE.SphereGeometry(0.045, 6, 4), pale, b);
    paw.position.set(k * 0.26, 0.68, -0.22);
    paw.scale.set(1, 0.7, 1.3);
  }
  const tail = put(new THREE.ConeGeometry(0.075, 0.46, 6), grey, b);
  tail.position.set(0.0, 0.8, -0.32);
  tail.rotation.x = Math.PI - 0.16;
  const tip = put(new THREE.ConeGeometry(0.045, 0.12, 5), dark, b);
  tip.position.set(0.0, 0.55, -0.36);
  tip.rotation.x = Math.PI - 0.16;
  // The forepaws, down from the shoulders over the chest, thick with fur, knotted together with red wool.
  for (const k of [-1, 1]) {
    const leg = put(new THREE.CylinderGeometry(0.05, 0.042, 0.26, 7), grey, b);
    leg.position.set(k * 0.12, 1.39, 0.19);
    leg.rotation.set(0.35, 0, k * 0.75);
    const paw = put(new THREE.SphereGeometry(0.05, 7, 5), pale, b);
    paw.position.set(k * 0.035, 1.3, 0.25);
    paw.scale.set(1.1, 0.75, 1.2);
    for (let c = -1; c <= 1; c++) {
      const claw = put(new THREE.ConeGeometry(0.008, 0.035, 3), nose, b);
      claw.position.set(k * 0.035 + c * 0.018, 1.265, 0.29);
      claw.rotation.x = Math.PI * 0.75;
    }
  }
  const knot = put(new THREE.SphereGeometry(0.04, 7, 5), red, b);
  knot.position.set(0, 1.31, 0.27);
  for (const k of [-1, 1]) {
    const end = put(new THREE.BoxGeometry(0.018, 0.09, 0.012), red, b);
    end.position.set(k * 0.02, 1.25, 0.275);
    end.rotation.z = k * 0.3;
  }
  // Bera's stitches where the skin meets at the throat: short red strokes.
  for (let i = 0; i < 6; i++) {
    const a = (i - 2.5) * 0.3;
    const st = put(new THREE.BoxGeometry(0.014, 0.05, 0.014), red, b);
    st.position.set(Math.sin(a) * 0.2, 1.5, Math.cos(a) * 0.15 - 0.01);
    st.rotation.x = 0.4;
  }
  // The head, big on the left shoulder (over the pauldron's iron) looking forward: skull, brow, ears up, the long muzzle, pale under the jaw,
  // the eyes empty and dark.
  const hd = G(b, -0.27, 1.64, 0.04);
  hd.rotation.y = -0.15;
  hd.scale.setScalar(1.35);
  const skull = put(new THREE.SphereGeometry(0.09, 10, 7), grey, hd);
  skull.scale.set(1, 0.8, 1.1);
  const brow = put(new THREE.SphereGeometry(0.065, 8, 5), dark, hd);
  brow.position.set(0, 0.04, -0.01);
  brow.scale.set(1.2, 0.6, 1.3);
  const cheek = put(new THREE.SphereGeometry(0.06, 8, 5), pale, hd);
  cheek.position.set(0, -0.04, 0.05);
  cheek.scale.set(1.3, 0.6, 1.1);
  const muzzle = put(new THREE.CylinderGeometry(0.03, 0.055, 0.16, 8), grey, hd);
  muzzle.position.set(0, -0.012, 0.13);
  muzzle.rotation.x = Math.PI / 2;
  const jaw = put(new THREE.BoxGeometry(0.06, 0.028, 0.13), pale, hd);
  jaw.position.set(0, -0.05, 0.11);
  put(new THREE.SphereGeometry(0.024, 6, 4), nose, hd).position.set(0, -0.003, 0.215);
  for (const k of [-1, 1]) {
    const ear = put(new THREE.ConeGeometry(0.034, 0.1, 4), dark, hd);
    ear.position.set(k * 0.05, 0.095, -0.03);
    ear.rotation.z = -k * 0.28;
    put(new THREE.SphereGeometry(0.014, 5, 3), nose, hd).position.set(k * 0.042, 0.022, 0.078);
    // A fang showing at the lip.
    const fang = put(new THREE.ConeGeometry(0.007, 0.03, 3), pale, hd);
    fang.position.set(k * 0.02, -0.045, 0.18);
    fang.rotation.x = Math.PI;
  }
};

/**
 * The cleric's pieces (docs/sendas/clerigo.md §10): 1, the shepherd's band: a thick band of raw wool tied round the
 * right upper arm, like the ones who climb the south face wear; chunky (two twists of wool), its knot and two tufted
 * ends hanging, a thin slate thread through it. The wool cold and pale, a little lit from inside, so it never turns cream
 * under the keep's warm light (docs/RENDIMIENTO.md); 2, the moon disc at the breast; 3, Ludo's fur collar round the neck;
 * 4, Aud's wick: a worn braided cord tied as a belt, a knot at the left hip and its two frayed ends hanging; 5, the whole
 * look: Tessa's stole, white with grey embroidery, round the neck and down the front to the knees; her small iron lantern
 * at the right hip, its panes lit white; and the light no other cleric has, a small one floating at the shoulder (a
 * sprite: no light of the scene's). The whites cold and a little lit from inside, so the bastion's fire never turns them
 * cream or pink.
 */
const dressClericPath = (rig: HeroRig, n: number, ghost: string | undefined): void => {
  const { put, mat } = putter(ghost);
  if (n >= 1) {
    const arm = absOf(rig.armR);
    const wool = mat(std('#d3d8df', { roughness: 1, emissive: '#3a4250', bumpScale: 0.03 }));
    const thread = mat(std('#3f4652', { roughness: 0.9 }));
    for (const dy of [0.02, -0.02]) ring(arm, 0.072, 0.024, wool, 1.27 + dy, 5, 14).position.set(0.255, 1.27 + dy, 0);
    ring(arm, 0.075, 0.006, thread, 1.27, 3, 14).position.set(0.255, 1.27, 0);
    const knot = put(new THREE.SphereGeometry(0.034, 7, 5), wool, arm);
    knot.position.set(0.33, 1.27, 0.02);
    for (const k of [-1, 1]) {
      const end = put(new THREE.ConeGeometry(0.022, 0.13, 5), wool, arm);
      end.position.set(0.35, 1.2, 0.02 + k * 0.022);
      end.rotation.z = Math.PI + k * 0.25;
    }
  }
  if (n >= 2) {
    // 2, the moon disc: a small mirror of polished steel on a dark cord at the breast, its face out, a rim of iron and a
    // pale cold sheen (lit a little from inside, so it reads on the board at night as the moon in a mirror).
    const b = absOf(rig.body);
    const steel = mat(std('#dfe6ef', { metalness: 0.85, roughness: 0.14, emissive: '#5d6f8f', emissiveIntensity: 0.55 }));
    const rim = mat(std('#3a3f47', { metalness: 0.6, roughness: 0.45 }));
    const cord = mat(std('#24272c', { roughness: 1 }));
    const disc = M(new THREE.CylinderGeometry(0.07, 0.07, 0.012, 18), steel, b);
    disc.rotation.x = Math.PI / 2 - 0.12;
    disc.position.set(0.0, 1.24, 0.215);
    const edge = M(new THREE.TorusGeometry(0.072, 0.009, 4, 18), rim, b);
    edge.rotation.x = -0.12;
    edge.position.set(0.0, 1.24, 0.215);
    for (const k of [-1, 1]) limb(b, V(k * 0.05, 1.3, 0.21), V(k * 0.09, 1.47, 0.12), 0.006, 0.006, cord, 4);
  }
  if (n >= 3) {
    // 3, Ludo's fur collar: a thick ring of grey-brown wolf fur round the neck and over the shoulders, tufted all round,
    // its tips paler. Cold grey-brown with a little cold light of its own, so it stays fur by the braziers and never
    // goes cream or orange under the warm light.
    const b = absOf(rig.body);
    const fur = mat(std('#6e655e', { roughness: 1, emissive: '#2a2f38', emissiveIntensity: 0.6 }));
    const tip = mat(std('#a9a6a4', { roughness: 1, emissive: '#3a4250', emissiveIntensity: 0.5 }));
    ring(b, 0.2, 0.07, fur, 1.46, 6, 16);
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2;
      const t = put(new THREE.ConeGeometry(0.035, 0.11, 5), i % 2 ? tip : fur, b);
      t.position.set(Math.cos(a) * 0.235, 1.44, Math.sin(a) * 0.2);
      t.rotation.set(Math.sin(a) * 1.2, 0, -Math.cos(a) * 1.2);
    }
  }
  if (n >= 4) {
    // 4, Aud's wick: a braided cord round the waist (two twisted strands), knotted at the left hip, its ends hanging frayed.
    const b = absOf(rig.body);
    const cord = mat(std('#b9bfc8', { roughness: 1, emissive: '#2e3440', emissiveIntensity: 0.6 }));
    const dark = mat(std('#6b717b', { roughness: 1 }));
    ring(b, 0.258, 0.016, cord, 0.9, 4, 18).position.y = 0.9;
    ring(b, 0.26, 0.009, dark, 0.875, 3, 18).position.y = 0.875;
    const knot = put(new THREE.SphereGeometry(0.032, 7, 5), cord, b);
    knot.position.set(-0.2, 0.88, 0.15);
    for (const k of [-1, 1]) {
      const end = limb(b, V(-0.2, 0.87, 0.16), V(-0.2 + k * 0.035, 0.66, 0.19), 0.012, 0.006, cord, 4);
      void end;
      put(new THREE.ConeGeometry(0.014, 0.05, 5), dark, b).position.set(-0.2 + k * 0.035, 0.645, 0.19);
    }
  }
  if (n >= 5) {
    // 5, Tessa's stole: two broad bands of white wool from the back of the neck over the shoulders and down the front, a
    // grey band of embroidery near each end and a fringe; her little lantern at the right hip; the floating light.
    const b = absOf(rig.body);
    const white = mat(std('#dbe2ec', { roughness: 0.95, emissive: '#4f5d72', emissiveIntensity: 0.7, side: THREE.DoubleSide }));
    const grey = mat(std('#6c7480', { roughness: 0.9, side: THREE.DoubleSide }));
    for (const k of [-1, 1]) {
      const band = put(new THREE.BoxGeometry(0.085, 0.72, 0.014), white, b);
      band.position.set(k * 0.1, 1.08, 0.235);
      band.rotation.z = k * 0.08;
      // Over the shoulder, a short piece toward the back.
      const over = put(new THREE.BoxGeometry(0.09, 0.012, 0.2), white, b);
      over.position.set(k * 0.12, 1.46, 0.13);
      for (const y of [0.83, 0.88]) put(new THREE.BoxGeometry(0.088, 0.014, 0.016), grey, b).position.set(k * 0.1 - k * 0.02 * (1.08 - y), y, 0.238);
      // A cross of grey stitched on each end.
      const cross = G(b, k * 0.098, 0.97, 0.244);
      put(new THREE.BoxGeometry(0.012, 0.06, 0.006), grey, cross);
      put(new THREE.BoxGeometry(0.04, 0.012, 0.006), grey, cross).position.y = 0.01;
      for (let i = 0; i < 4; i++) put(new THREE.BoxGeometry(0.008, 0.045, 0.008), white, b).position.set(k * 0.1 - 0.03 + i * 0.02, 0.7, 0.236);
    }
    // The lantern: a small cage of black iron, its panes lit white, a ring to hang it by, at the right hip.
    const at = G(b, 0.255, 0.64, 0.1);
    const iron = std('#26292f', { roughness: 0.55, metalness: 0.5, flatShading: true });
    put(new THREE.TorusGeometry(0.02, 0.005, 3, 8), iron, at).position.y = 0.085;
    const cage = put(new THREE.CylinderGeometry(0.04, 0.045, 0.1, 6), iron, at);
    cage.rotation.y = Math.PI / 6;
    put(new THREE.CylinderGeometry(0.03, 0.03, 0.075, 6), mat(std('#ffffff', { roughness: 0.3, emissive: '#eef3ff' })), at);
    put(new THREE.ConeGeometry(0.048, 0.04, 6), iron, at).position.y = 0.07;
    if (!ghost) {
      glowSprite(at, '#f4f8ff', 0.34, V(0, 0, 0.02), 0.8);
      // The small light at the shoulder, like the stretcher's lantern: a cold white point and its halo.
      glowSprite(b, '#ffffff', 0.16, V(0.34, 1.62, 0.12), 0.95);
      glowSprite(b, '#dfe8ff', 0.5, V(0.34, 1.62, 0.12), 0.35);
    }
  }
};

/**
 * The artificer's pieces (docs/sendas/artifice.md §10): 1, Nadd's belt of keys: a ring of bronze keys hanging at the left
 * hip from the belt, gone green at the bows, big for their size so they read on the board (a pale glint on each, lit a
 * little); 2, the smoked-glass goggles pushed up on the brow: a dark leather strap round the head, two round frames of
 * blackened iron, one lens smoked dark and the other red (lit a little, so the red reads from the board's height); 3,
 * the iron glove with Nadd's mark on the right hand, bigger than the other: a cuff of riveted plate over the wrist,
 * plates over the back of the hand and the knuckles, and on the back Nadd's mark in the dragon's red (a ring and its
 * stroke), lit a little; 4, the pack with the tube of dust on the back, its glass beating red; 5, Nadd's mark painted
 * red on the chest and Grillo's lamp, dark, at the belt (and Tormo, who walks with the hero).
 */
/**
 * The mage's pieces (docs/sendas/mago.md §10): 1, the burnt clasp of the Book of the Tides, hung by a short chain from
 * the head of the staff (its weapon), bronze gone black, an ember thread on it; the others come with their chapters.
 */
const dressMagePath = (rig: HeroRig, n: number, ghost: string | undefined): void => {
  if (n < 1) return;
  const at = G(absOf(rig.weapon), 0, 0.62, 0.04);
  const g = burntClasp(at);
  g.scale.setScalar(1.6);
  if (ghost) {
    const { mat } = putter(ghost);
    g.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) m.material = mat(m.material as THREE.Material);
    });
  }
};

const dressArtificerPath = (rig: HeroRig, n: number, ghost: string | undefined): void => {
  const { put, mat } = putter(ghost);
  if (n >= 1) {
    const b = absOf(rig.body);
    const at = G(b, -0.2, 0.9, 0.1);
    const bronze = mat(std('#6a5a44', { roughness: 0.45, metalness: 0.55, emissive: '#1a1510' }));
    const green = mat(std('#5d8a7c', { roughness: 0.7 }));
    put(new THREE.TorusGeometry(0.06, 0.012, 4, 12), bronze, at);
    for (let i = 0; i < 5; i++) {
      const key = G(at, -0.05 + i * 0.025, -0.07, 0.01);
      key.rotation.z = (i - 2) * 0.2;
      put(new THREE.BoxGeometry(0.014, 0.11, 0.008), bronze, key).position.y = -0.04;
      put(new THREE.TorusGeometry(0.018, 0.006, 3, 8), i % 2 ? green : bronze, key).position.y = 0.025;
      put(new THREE.BoxGeometry(0.03, 0.012, 0.008), bronze, key).position.y = -0.085;
    }
  }
  if (n >= 2) {
    const hd = absOf(rig.head);
    const at = G(hd, 0, 0.115, 0.035);
    const strap = mat(std('#2a2420', { roughness: 0.9 }));
    const iron = mat(std('#1d1f23', { roughness: 0.4, metalness: 0.6 }));
    const smoke = mat(std('#15171b', { roughness: 0.15, metalness: 0.2 }));
    const red = mat(std('#c23a2a', { roughness: 0.2, emissive: '#6a140c' }));
    const band = put(new THREE.TorusGeometry(0.105, 0.012, 4, 18), strap, at);
    band.rotation.x = Math.PI / 2 - 0.25;
    for (const k of [-1, 1]) {
      const eye = G(at, k * 0.045, 0.01, 0.09);
      eye.rotation.x = -0.6;
      put(new THREE.TorusGeometry(0.028, 0.008, 5, 12), iron, eye);
      put(new THREE.CircleGeometry(0.026, 12), k < 0 ? smoke : red, eye).position.z = 0.002;
    }
  }
  if (n >= 3 && rig.foreR) {
    const fr = absOf(rig.foreR);
    const iron = mat(std('#3a3e45', { roughness: 0.38, metalness: 0.7 }));
    const dark = mat(std('#1f2226', { roughness: 0.5, metalness: 0.6 }));
    const mark = mat(std('#d23a28', { roughness: 0.3, emissive: '#7a160c' }));
    // The cuff over the wrist, two bands of rivets.
    const cuff = put(new THREE.CylinderGeometry(0.058, 0.07, 0.15, 10), iron, fr);
    cuff.position.set(0.28, 0.99, 0.12);
    for (const y of [0.93, 1.05]) {
      const b = put(new THREE.TorusGeometry(0.066, 0.008, 4, 12), dark, fr);
      b.position.set(0.28, y, 0.12);
      b.rotation.x = Math.PI / 2;
    }
    // The hand: plates over its back and the knuckles, big for its size (the right hand, bigger than the other).
    const hand = G(fr, 0.28, 0.88, 0.14);
    put(new THREE.BoxGeometry(0.1, 0.1, 0.075), iron, hand);
    const kn = put(new THREE.BoxGeometry(0.105, 0.035, 0.08), dark, hand);
    kn.position.set(0, -0.06, 0.005);
    for (let i = 0; i < 4; i++) put(new THREE.BoxGeometry(0.022, 0.05, 0.022), iron, hand).position.set(-0.036 + i * 0.024, -0.095, 0.02);
    // Nadd's mark on the back of the hand: a ring and its stroke, red.
    const m = G(hand, 0, 0.005, 0.04);
    put(new THREE.TorusGeometry(0.022, 0.006, 3, 12), mark, m);
    put(new THREE.BoxGeometry(0.008, 0.05, 0.006), mark, m);
  }
  if (n >= 4) {
    // 4, the pack with the tube of dust: a leather pack high on the back, straps over the shoulders, and standing in it
    // a glass cylinder in an iron cage that beats red (lit well: from the board's height it reads as a red light).
    const b = absOf(rig.body);
    const leather = mat(std('#3a2c24', { roughness: 0.85 }));
    const iron = mat(std('#4a5058', { roughness: 0.4, metalness: 0.65 }));
    const glass = mat(std('#ff5a3a', { roughness: 0.15, emissive: '#d82a14', emissiveIntensity: 1.6, transparent: true, opacity: 0.9 }));
    const pack = put(new THREE.BoxGeometry(0.26, 0.3, 0.12), leather, b);
    pack.position.set(0, 1.2, -0.31);
    for (const k of [-1, 1]) {
      const st = put(new THREE.BoxGeometry(0.035, 0.42, 0.02), leather, b);
      st.position.set(k * 0.1, 1.3, -0.25);
      st.rotation.x = 0.3;
    }
    const tube = G(b, 0.0, 1.36, -0.39);
    put(new THREE.CylinderGeometry(0.055, 0.055, 0.36, 12), glass, tube);
    for (const y of [-0.19, 0.19]) put(new THREE.CylinderGeometry(0.068, 0.068, 0.04, 12), iron, tube).position.y = y;
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      put(new THREE.BoxGeometry(0.012, 0.38, 0.012), iron, tube).position.set(Math.cos(a) * 0.064, 0, Math.sin(a) * 0.064);
    }
  }
  if (n >= 5) {
    // 5, Nadd's mark painted big in red on the chest (a ring and its stroke, lit a little: from the board's height it is
    // what says «de Tormo»), and Grillo's lamp hanging from the belt at the right hip, its glass dark (it went out at
    // the door): a small bronze housing, a cracked dark-red lens, a ring to hang it by.
    const b = absOf(rig.body);
    const red = mat(std('#ff4f3a', { roughness: 0.6, emissive: '#8a1c10', side: THREE.DoubleSide }));
    const mark = G(b, 0, 1.22, 0.255);
    mark.rotation.x = -0.1;
    put(new THREE.TorusGeometry(0.07, 0.017, 4, 18), red, mark);
    const stroke = put(new THREE.BoxGeometry(0.026, 0.12, 0.01), red, mark);
    stroke.position.y = -0.11;
    const bronze = mat(std('#5c5246', { roughness: 0.5, metalness: 0.55 }));
    const lamp = G(b, 0.2, 0.84, 0.12);
    lamp.rotation.z = 0.12;
    put(new THREE.TorusGeometry(0.02, 0.005, 4, 10), bronze, lamp).position.y = 0.09;
    put(new THREE.CylinderGeometry(0.05, 0.055, 0.1, 10), bronze, lamp);
    const lens = put(new THREE.SphereGeometry(0.034, 10, 8), mat(std('#3a0f0a', { roughness: 0.2, metalness: 0.2 })), lamp);
    lens.position.set(0, 0, 0.04);
    lens.scale.z = 0.6;
    put(new THREE.BoxGeometry(0.004, 0.05, 0.004), mat(std('#c3cad3', { roughness: 0.5 })), lamp).position.set(0.01, 0.005, 0.062);
  }
};

/**
 * The druid's pieces (docs/sendas/druida.md §10): 1, the first leaf on the antlers: a new leaf of spring, pale green
 * with a lighter vein, sprouted out of the left antler's first fork and turned out to the side, big for its size and a
 * little lit so it reads green (not grey) at the game's distance; the others come with their seasons.
 */
const dressDruidPath = (rig: HeroRig, n: number, ghost: string | undefined): void => {
  const { put, mat } = putter(ghost);
  if (n >= 1) {
    const h = absOf(rig.head);
    const at = G(h, -0.21, 1.93, -0.04);
    at.rotation.set(0.2, 0.5, 0.9);
    const green = mat(std('#9ee06a', { roughness: 0.65, emissive: '#2f5a14', emissiveIntensity: 0.7 }));
    const stem = put(new THREE.CylinderGeometry(0.006, 0.008, 0.05, 4), std('#6a8a3c', { roughness: 0.9 }), at);
    stem.position.y = 0.02;
    // The blade of the leaf: a flattened, pointed oval; its vein down the middle.
    const leaf = put(new THREE.SphereGeometry(0.06, 10, 6), green, at);
    leaf.scale.set(0.55, 1.35, 0.14);
    leaf.position.y = 0.11;
    const vein = put(new THREE.BoxGeometry(0.006, 0.14, 0.006), std('#d8f5b8', { roughness: 0.7, emissive: '#3c5a22' }), at);
    vein.position.set(0, 0.11, 0.01);
    // A curl of a second, smaller one coming out beside it.
    const bud = put(new THREE.SphereGeometry(0.03, 8, 5), green, at);
    bud.scale.set(0.5, 1.2, 0.2);
    bud.position.set(0.04, 0.06, 0);
    bud.rotation.z = -0.7;
  }
  // 2, summer: the second leaf on the right antler, dry yellow with a brown edge, curling; the antlers' tips sooty
  // from the counterfire (black caps a little glossy, so they read against the hood).
  if (n >= 2) {
    const h = absOf(rig.head);
    const at = G(h, 0.21, 1.93, -0.04);
    at.rotation.set(0.2, -0.5, -0.9);
    const dry = mat(std('#d2b048', { roughness: 0.8, emissive: '#4a3810', emissiveIntensity: 0.5 }));
    const stem = put(new THREE.CylinderGeometry(0.006, 0.008, 0.05, 4), std('#7a6a3c', { roughness: 0.9 }), at);
    stem.position.y = 0.02;
    const leaf = put(new THREE.SphereGeometry(0.06, 10, 6), dry, at);
    leaf.scale.set(0.5, 1.3, 0.14);
    leaf.position.y = 0.11;
    leaf.rotation.y = 0.3;
    const edge = put(new THREE.SphereGeometry(0.062, 10, 6), std('#7a5426', { roughness: 0.9 }), at);
    edge.scale.set(0.46, 1.24, 0.1);
    edge.position.set(0, 0.11, -0.004);
    const vein = put(new THREE.BoxGeometry(0.006, 0.13, 0.006), std('#8a6a2c', { roughness: 0.8 }), at);
    vein.position.set(0, 0.11, 0.01);
    const soot = mat(std('#121112', { roughness: 0.35, metalness: 0.15 }));
    for (const k of [-1, 1])
      for (const [x, y, z] of [
        [0.33, 2.19, -0.02],
        [0.27, 2.08, 0.06],
        [0.2, 1.98, 0.08],
      ] as const) {
        const tip = put(new THREE.SphereGeometry(0.02, 6, 5), soot, G(h, k * x, y, z));
        tip.scale.set(1, 2.2, 1);
      }
  }
  // 3, autumn: the third leaf on the left antler, a maple's, red with five points and a darker vein; a necklace of
  // acorns on a cord round the neck (their cups dark, the nuts warm brown, a pale gloss so they read on the robe).
  if (n >= 3) {
    const h = absOf(rig.head);
    const at = G(h, -0.29, 2.07, -0.03);
    at.rotation.set(0.15, 0.4, 1.05);
    const red = mat(std('#c0321e', { roughness: 0.7, emissive: '#4a0e08', emissiveIntensity: 0.55 }));
    put(new THREE.CylinderGeometry(0.006, 0.008, 0.05, 4), std('#6a2a1a', { roughness: 0.9 }), at).position.y = 0.02;
    for (let i = 0; i < 5; i++) {
      const a = ((i - 2) / 2) * 1.0;
      const lobe = put(new THREE.ConeGeometry(0.03, 0.1, 4), red, at);
      lobe.position.set(Math.sin(a) * 0.04, 0.09 + Math.cos(a) * 0.04, 0);
      lobe.rotation.z = -a;
      lobe.scale.z = 0.3;
    }
    put(new THREE.BoxGeometry(0.005, 0.1, 0.006), std('#7a1a10', { roughness: 0.8 }), at).position.set(0, 0.09, 0.008);
    const b = absOf(rig.body);
    const cord = std('#3a2a1e', { roughness: 1 });
    const nut = mat(std('#8a5a2c', { roughness: 0.4, metalness: 0.05 }));
    const cup = mat(std('#3e2c1c', { roughness: 0.9 }));
    for (let i = 0; i < 9; i++) {
      const a = ((i - 4) / 4) * 1.15;
      const g = G(b, Math.sin(a) * 0.17, 1.38 - Math.cos(a) * 0.06 - (4 - Math.abs(i - 4)) * 0.012, 0.13 + Math.cos(a) * 0.05);
      put(new THREE.SphereGeometry(0.006, 4, 3), cord, g);
      if (i % 2 === 0) {
        const acorn = put(new THREE.SphereGeometry(0.022, 7, 5), nut, g);
        acorn.scale.set(0.85, 1.2, 0.85);
        acorn.position.y = -0.022;
        put(new THREE.SphereGeometry(0.02, 7, 4, 0, Math.PI * 2, 0, Math.PI * 0.5), cup, g).position.y = -0.006;
      }
    }
  }
  // 4, winter: the fourth leaf, high on the right antler, white with frost (an oak's, lobed, a cold pale-blue glow so it
  // reads on the hood under warm light); a cloak of leaves on the shoulders and down the back, dark green and brown,
  // the frost stuck white along their edges.
  if (n >= 4) {
    const h = absOf(rig.head);
    const at = G(h, 0.28, 2.08, -0.05);
    at.rotation.set(0.1, -0.4, -1.1);
    const frost = mat(std('#f4f8fc', { roughness: 0.5, emissive: '#8ab4dc', emissiveIntensity: 0.55 }));
    put(new THREE.CylinderGeometry(0.006, 0.008, 0.05, 4), std('#8a9aa8', { roughness: 0.9 }), at).position.y = 0.02;
    for (let i = 0; i < 4; i++) {
      const lobe = put(new THREE.SphereGeometry(0.03, 7, 5), frost, at);
      lobe.scale.set(1.1, 0.9, 0.22);
      lobe.position.set((i % 2 ? 1 : -1) * 0.028, 0.07 + i * 0.026, 0);
    }
    put(new THREE.SphereGeometry(0.028, 7, 5), frost, at).position.set(0, 0.17, 0);
    put(new THREE.BoxGeometry(0.005, 0.14, 0.006), std('#c4d8ec', { roughness: 0.6 }), at).position.set(0, 0.1, 0.008);
    const b = absOf(rig.body);
    const leaves = [mat(std('#3e5a2c', { roughness: 0.85 })), mat(std('#5a4a2a', { roughness: 0.85 })), mat(std('#4a6234', { roughness: 0.85 }))];
    const rime = mat(std('#eef4fa', { roughness: 0.6, emissive: '#6a8ab0', emissiveIntensity: 0.25 }));
    for (let row = 0; row < 3; row++)
      for (let i = 0; i < 7 - row; i++) {
        const a = ((i - (6 - row) / 2) / 3.2) * 1.5 + Math.PI;
        const g = G(b, Math.sin(a) * (0.2 - row * 0.01), 1.42 - row * 0.17, Math.cos(a) * (0.16 + row * 0.015) - 0.01);
        g.rotation.set(0.25 + row * 0.08, a, ((i % 2) - 0.5) * 0.4);
        const lf = put(new THREE.SphereGeometry(0.06, 7, 5), leaves[(i + row) % 3] as THREE.Material, g);
        lf.scale.set(0.75, 1.25, 0.16);
        const fr = put(new THREE.SphereGeometry(0.062, 7, 5), rime, g);
        fr.scale.set(0.8, 0.35, 0.12);
        fr.position.set(0, 0.055, -0.004);
      }
  }
  // 5, the whole look: the antlers bloom with the season (green in spring, ochre in summer, red in autumn, white in
  // winter): clusters of small leaves and flowers all along both antlers, lit a little, a soft halo of the season's
  // colour round them (a glow, baked) so the game's camera finds them on a phone. (Its leaves where the druid treads:
  // engine/world.ts, a fixed few instances.)
  if (n >= 5) {
    const h = absOf(rig.head);
    const pal = SEASON_BLOOM[druidSeason()] as (typeof SEASON_BLOOM)[number];
    const petal = mat(std(pal.petal, { roughness: 0.6, emissive: pal.glow, emissiveIntensity: 0.55 }));
    const leafM = mat(std(pal.leaf, { roughness: 0.7, emissive: pal.glow, emissiveIntensity: 0.3 }));
    const heart = mat(std(pal.heart, { roughness: 0.5, emissive: pal.heart, emissiveIntensity: 0.4 }));
    for (const k of [-1, 1])
      for (const [x, y, z, sz] of [
        [0.33, 2.2, -0.02, 1.15],
        [0.27, 2.09, 0.06, 1.0],
        [0.2, 1.99, 0.08, 0.9],
        [0.26, 2.13, -0.06, 0.95],
        [0.3, 2.02, -0.07, 0.85],
        [0.22, 2.05, 0.0, 0.8],
      ] as const) {
        const g = G(h, k * x, y, z);
        g.rotation.set(0.3, k * 0.6, k * 0.4);
        for (let i = 0; i < 5; i++) {
          const a = (i / 5) * Math.PI * 2;
          const pe = put(new THREE.SphereGeometry(0.022 * sz, 6, 4), petal, g);
          pe.position.set(Math.cos(a) * 0.024 * sz, Math.sin(a) * 0.024 * sz, 0);
          pe.scale.set(1.2, 1.2, 0.4);
        }
        put(new THREE.SphereGeometry(0.012 * sz, 5, 4), heart, g);
        const lf = put(new THREE.SphereGeometry(0.03 * sz, 7, 5), leafM, g);
        lf.scale.set(0.5, 1.4, 0.15);
        lf.position.set(k * 0.03, -0.035, -0.01);
        lf.rotation.z = k * 0.8;
      }
    if (!ghost) {
      glowSprite(h, pal.glow, 0.7, V(0.27, 2.08, 0.02), 0.32);
      glowSprite(h, pal.glow, 0.7, V(-0.27, 2.08, 0.02), 0.32);
    }
  }
};

/**
 * The season the druid's last look blooms with (docs/sendas/druida.md §10): from the date, in the drawing only (the
 * sim never sees it: nothing of a fight changes). 0 spring, 1 summer, 2 autumn, 3 winter (the meteorological ones of
 * the north, as the path's year goes). The client can set it from the campaign's own date.
 */
let SEASON_SET: number | null = null;
export const setDruidSeason = (n: number | null): void => {
  SEASON_SET = n;
};
export const druidSeason = (): number => {
  if (SEASON_SET !== null) return SEASON_SET;
  const m = new Date().getMonth();
  return m >= 2 && m <= 4 ? 0 : m >= 5 && m <= 7 ? 1 : m >= 8 && m <= 10 ? 2 : 3;
};
/** The bloom by season: its petals, the leaves under them, the flower's heart, its glow (the leaves it drops too). */
export const SEASON_BLOOM = [
  { petal: '#c8f0a0', leaf: '#6ec048', heart: '#f0f8c0', glow: '#9ee070' },
  { petal: '#e8b848', leaf: '#b08a2a', heart: '#fff0a0', glow: '#e8b040' },
  { petal: '#e85a3a', leaf: '#a8301e', heart: '#ffc890', glow: '#ff6a3a' },
  { petal: '#e8f2fc', leaf: '#a8c4dc', heart: '#ffffff', glow: '#bfe4ff' },
] as const;

/**
 * The ranger's pieces (docs/sendas/explorador.md §10): 1, Aster's knotted cord at the belt: a loop of red cord tied at
 * the right hip and three strands hanging from it, each with its knots (simple ones, a turn, a tight one), big for their
 * size so they read on the board, the red a little lit; 2, the walker's coil of rope over the left shoulder, grey with
 * a red end. Other chapters' pieces come with them.
 */
const dressRangerPath = (rig: HeroRig, n: number, ghost: string | undefined): void => {
  const { put, mat } = putter(ghost);
  if (n >= 1) {
    const b = absOf(rig.body);
    const red = mat(std('#c2352a', { roughness: 0.85, emissive: '#3a0c08' }));
    const at = G(b, 0.2, 0.94, 0.08);
    put(new THREE.TorusGeometry(0.045, 0.014, 4, 12), red, at).rotation.y = 0.9;
    for (let i = 0; i < 3; i++) {
      const strand = G(at, -0.03 + i * 0.03, -0.02, 0.02);
      strand.rotation.z = (i - 1) * 0.18;
      put(new THREE.CylinderGeometry(0.008, 0.008, 0.2 + i * 0.03, 4), red, strand).position.y = -0.1 - i * 0.015;
      for (let k = 0; k < 2 + i; k++) put(new THREE.SphereGeometry(i === 2 && k === 0 ? 0.024 : 0.017, 6, 5), red, strand).position.y = -0.05 - k * 0.055;
    }
  }
  // 2, the walker's coil of rope: grey, over the left shoulder and across the chest, its end red, a loop tied off.
  if (n >= 2) {
    const b = absOf(rig.body);
    const grey = mat(std('#8a9096', { roughness: 0.95 }));
    const redEnd = mat(std('#c2352a', { roughness: 0.85, emissive: '#3a0c08' }));
    const at = G(b, -0.06, 1.22, 0.03);
    for (const [r, t] of [
      [0.17, 0.04],
      [0.15, 0.035],
      [0.13, 0.03],
    ] as [number, number][]) {
      const coil = put(new THREE.TorusGeometry(r, t, 6, 16), grey, at);
      coil.rotation.set(0.15, 0, -0.8);
      coil.scale.set(1, 1.3, 1);
    }
    const end = put(new THREE.CylinderGeometry(0.02, 0.02, 0.16, 5), redEnd, at);
    end.position.set(0.12, -0.16, 0.05);
    end.rotation.z = 0.4;
  }
};

/**
 * The paladin's pieces (docs/sendas/paladin.md §10): 1, the glass eye: a shard of the Burn's black glass set in the
 * shield's face, an open eye cut in it, its iris the Vigil's red, a rim of pale stone round it (it reads on the shield
 * from the game's camera: a dark lozenge with a red dot); 2, the salt hem (the tabard's border, white as salt); 3, the
 * bridge lantern (an iron lantern at the belt, white light); 4, the eye brooch (white, on the cloak); 5, the Way's tabard
 * (white, the open eye in red) and the candle on the shield.
 */
const dressPaladinPath = (rig: HeroRig, n: number, ghost: string | undefined): void => {
  const { put, mat } = putter(ghost);
  if (n >= 1) {
    const off = rig.offhand;
    const at = G(off, -0.06, 0.06, 0.085);
    at.rotation.set(0, -0.35, 0);
    // The pale setting, then the shard of black glass in it, cut in facets.
    put(new THREE.CylinderGeometry(0.085, 0.085, 0.012, 6), std('#c9ccd2', { roughness: 0.8, flatShading: true }), at).rotation.x = Math.PI / 2;
    const shard = put(new THREE.OctahedronGeometry(0.075, 0), std('#0d0c12', { roughness: 0.15, metalness: 0.4, flatShading: true }), at);
    shard.scale.set(1.0, 1.25, 0.32);
    shard.position.z = 0.012;
    // The eye cut in it: a pale almond and the red iris.
    const lid = put(new THREE.TorusGeometry(0.036, 0.006, 4, 12), std('#d6d9de', { roughness: 0.6 }), at);
    lid.scale.set(1, 0.55, 1);
    lid.position.z = 0.04;
    const iris = put(new THREE.CircleGeometry(0.014, 10), mat(std('#ff4f3a', { roughness: 0.4, emissive: '#7a1a10' })), at);
    iris.position.z = 0.042;
  }
  if (n >= 2) {
    // 2, the salt hem: the tabard's border white as salt, a band round the hips and down the split at the front, its
    // crust catching the light (a little lit, so it reads white at the game's distance).
    const b = absOf(rig.body);
    const salt = mat(std('#f4f6f8', { roughness: 0.7, emissive: '#3a3f46' }));
    const hem = M(new THREE.CylinderGeometry(0.262, 0.268, 0.055, 14, 1, true), salt, b);
    hem.position.y = 0.66;
    (hem.material as THREE.MeshStandardMaterial).side = THREE.DoubleSide;
    for (const k of [-1, 1]) {
      const edge = put(new THREE.BoxGeometry(0.03, 0.36, 0.012), std('#f4f6f8', { roughness: 0.7, emissive: '#3a3f46' }), b);
      edge.position.set(k * 0.06, 0.86, 0.22);
    }
  }
  if (n >= 3) {
    // 3, the bridge lantern: an iron lantern hung at the belt on the right hip, its four panes lit white (lit from
    // within: no light of its own). Large for its size and with a white halo (a sprite, baked with the model), or at
    // the game's distance it was a grey speck nobody saw.
    const b = absOf(rig.body);
    const at = G(b, 0.25, 0.6, 0.12);
    const iron = std('#2a2e35', { roughness: 0.55, metalness: 0.5, flatShading: true });
    put(new THREE.BoxGeometry(0.01, 0.1, 0.01), iron, at).position.y = 0.09;
    const cage = put(new THREE.CylinderGeometry(0.052, 0.058, 0.13, 4), iron, at);
    cage.rotation.y = Math.PI / 4;
    const glow = put(new THREE.BoxGeometry(0.066, 0.1, 0.066), mat(std('#ffffff', { roughness: 0.3, emissive: '#f2f6ff' })), at);
    glow.position.y = 0.002;
    const cap = put(new THREE.ConeGeometry(0.062, 0.05, 4), iron, at);
    cap.position.y = 0.09;
    cap.rotation.y = Math.PI / 4;
    if (!ghost) glowSprite(at, '#f4f8ff', 0.42, V(0, 0, 0.02), 0.85);
  }
  if (n >= 4) {
    // 4, the eye brooch: a white brooch with an open eye, pinning the cloak at the left shoulder's front: a pale disc
    // (lit a little, so it stays white at dusk), the almond of the eye in slate and the Vigil's red at its middle.
    const b = absOf(rig.body);
    const at = G(b, -0.13, 1.4, 0.2);
    at.rotation.set(-0.15, 0.25, 0);
    put(new THREE.CylinderGeometry(0.068, 0.068, 0.018, 16), mat(std('#f4f5f6', { roughness: 0.5, emissive: '#6a7078' })), at).rotation.x = Math.PI / 2;
    const lid = put(new THREE.TorusGeometry(0.04, 0.008, 4, 14), std('#3a3f48', { roughness: 0.6 }), at);
    lid.scale.set(1, 0.52, 1);
    lid.position.z = 0.011;
    put(new THREE.CircleGeometry(0.016, 10), mat(std('#ff4f3a', { roughness: 0.4, emissive: '#7a1a10' })), at).position.z = 0.012;
    if (!ghost) glowSprite(at, '#ffffff', 0.18, V(0, 0, 0.03), 0.35);
  }
  if (n >= 5) {
    // 5, the Way's tabard: white over whatever the class wears, front and back panels from the chest to the thighs (lit a
    // little, so it reads white at night), the open eye large on the breast in dragon red; and on the shield, the candle
    // that never goes out: a white flame on an iron pricket, leaning with the wind (its shader; no light of its own), a
    // white halo round it (a glow, baked) so the game's camera finds it on a phone.
    const b = absOf(rig.body);
    // (A cold white, its glow cold too: a warm fire or the bastion's warm key turned a neutral white to cream.)
    const white = mat(std('#d0dae7', { roughness: 0.9, emissive: '#5f6f86', side: THREE.DoubleSide }));
    const trim = mat(std('#353b45', { roughness: 0.8, side: THREE.DoubleSide }));
    for (const back of [0, Math.PI]) {
      const panel = M(new THREE.CylinderGeometry(0.248, 0.292, 0.84, 10, 1, true, back - 0.82, 1.64), white, b);
      panel.position.y = 1.02;
      // A slate hem, so the cloth's edge reads against the plate below it.
      const hem = M(new THREE.CylinderGeometry(0.294, 0.296, 0.035, 10, 1, true, back - 0.83, 1.66), trim, b);
      hem.position.y = 0.615;
    }
    // Its hem split at the sides, a thin dark line at the waist (the belt shows through).
    M(new THREE.CylinderGeometry(0.262, 0.262, 0.035, 12, 1, true), mat(std('#2a2d33', { roughness: 0.8 })), b).position.y = 0.94;
    const eye = G(b, 0, 1.2, 0.262);
    eye.rotation.x = -0.08;
    const red = mat(std('#ff4f3a', { roughness: 0.5, emissive: '#8a1c10' }));
    // (Large: on a phone at the game's camera the eye is what says «the Way».)
    const lid = put(new THREE.TorusGeometry(0.125, 0.021, 4, 16), red, eye);
    lid.scale.set(1, 0.55, 0.6);
    put(new THREE.CircleGeometry(0.055, 14), red, eye).position.z = 0.006;
    put(new THREE.CircleGeometry(0.018, 8), mat(std('#16181c', { roughness: 0.6 })), eye).position.z = 0.008;
    if (!ghost) {
      const off = rig.offhand;
      const at = G(off, -0.06, 0.2, 0.12);
      const iron = std('#2a2e35', { roughness: 0.55, metalness: 0.5 });
      M(new THREE.CylinderGeometry(0.03, 0.02, 0.02, 6), iron, at).position.y = -0.01;
      M(new THREE.CylinderGeometry(0.02, 0.02, 0.07, 8), std('#eef0f2', { roughness: 0.7, emissive: '#50555c' }), at).position.y = 0.035;
      for (const yaw of [0, Math.PI / 2]) {
        const fl = M(new THREE.PlaneGeometry(0.08, 0.17), candleMat(), at);
        fl.position.y = 0.155;
        fl.rotation.y = yaw;
      }
      glowSprite(at, '#ffffff', 0.42, V(0, 0.15, 0.02), 0.7);
    }
  }
};

/** The clock the candle's flame leans by (the view sets it every frame; one number for every candle). */
export const CANDLE_TIME: THREE.IUniform<number> = { value: 0 };
let CANDLE: THREE.MeshBasicMaterial | null = null;
/**
 * The Way's candle (the paladin's chapter 5): a white flame drawn on two crossed quads, its core white, its edge a cold
 * blue-white; it sways and leans with the wind (by the time, in its vertex shader: its top moves, its foot stays), never
 * goes out. Made once and shared (the bake keeps it: transparent, its own material).
 */
const candleMat = (): THREE.MeshBasicMaterial => {
  if (CANDLE) return CANDLE;
  const m = new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, depthWrite: false, side: THREE.DoubleSide, toneMapped: false });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uCandleT = CANDLE_TIME;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uCandleT;\nvarying vec2 vCuv;')
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vCuv = uv;
float lean = sin(uCandleT * 1.3 + transformed.z * 30.0) * 0.6 + sin(uCandleT * 3.7) * 0.25 + 0.35;
transformed.x += lean * uv.y * uv.y * 0.045;
transformed.y += uv.y * 0.012 * sin(uCandleT * 9.0);`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uCandleT;\nvarying vec2 vCuv;')
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
{
  vec2 p = vCuv - vec2(0.5, 0.0);
  // A drop: round at its foot, drawn up to a point.
  float y = vCuv.y;
  float w = 0.42 * sqrt(max(0.0, y * 3.2)) * (1.0 - y) * 1.35;
  float d = abs(p.x) / max(0.001, w);
  float body = (1.0 - smoothstep(0.7, 1.0, d)) * smoothstep(0.0, 0.08, y) * (1.0 - smoothstep(0.85, 1.0, y));
  float core = (1.0 - smoothstep(0.2, 0.6, d)) * (1.0 - smoothstep(0.35, 0.7, y));
  diffuseColor.rgb = mix(vec3(0.78, 0.86, 1.0), vec3(1.0), core);
  diffuseColor.a = body * (0.75 + 0.25 * core);
}`,
      );
  };
  m.customProgramCacheKey = () => 'somnhold-candle';
  CANDLE = m;
  return m;
};

let PLUME: THREE.Texture | null = null;
/** A soft wedge of white for the cloak's plume: dense at the hem, gone at its tip (made once). */
const plumeTexture = (): THREE.Texture => {
  if (PLUME) return PLUME;
  const w = 64;
  const h = 32;
  const data = new Uint8Array(w * h * 4);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const u = x / (w - 1);
      const v = Math.abs(y / (h - 1) - 0.5) * 2;
      const a = Math.max(0, 1 - v * (0.6 + u * 0.8)) * (1 - u) * (0.75 + 0.25 * Math.sin(x * 0.9 + y * 1.7));
      const i = (y * w + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = Math.round(255 * Math.max(0, Math.min(1, a)));
      data[i + 3] = 255;
    }
  PLUME = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
  PLUME.needsUpdate = true;
  return PLUME;
};

/**
 * Dresses the hero in its look: the hands (dressHands), the head piece in place of the class's hat or helm,
 * the chest's weight on the torso and shoulders, and the boots. A slot with nothing worn, or a unique piece,
 * keeps the class's own model.
 */
export const dressHero = (m: Model, cls: HeroClass, look: Look, ghost?: string): void => {
  const rig = m.rig;
  if (!rig) return;
  dressHands(rig, cls, look, ghost);
  const worn = (p: GearBaseId | 'u' | null): GearBaseId | null => (p && p !== 'u' ? p : null);
  const head = worn(look.head), chest = worn(look.chest), feet = worn(look.feet);
  if (head) dressHead(rig, cls, head, ghost);
  if (chest) dressChest(rig, chest, ghost);
  if (feet) dressFeet(rig, feet, ghost);
  if (look.senda > 0) dressPath(rig, cls, look.senda, ghost);
  // The titan's mark (docs/EQUIPO.md §7): a dragon-red bloom round the body and a pale one at the heart (glow sprites, no light).
  if (look.titan && !ghost) {
    aura(rig.figure, '#ff4f3a');
    glowSprite(rig.figure, '#ffd2c8', 0.9, V(0, 1.25, 0.12), 0.22);
  }
};
